<!--
  Galería de plantillas de informe. Pinta las tarjetas desde el data module y
  emite la plantilla elegida (objeto completo) por `use-template`.
  Los tokens --rt-* los hereda del wrapper .report-templates del orquestador.
-->
<template>
  <section>
    <p class="rt-lead">{{ $t('report_templates_lead') }}</p>
    <div class="rt-gallery">
      <div v-for="t in templatesArr" :key="t.key" class="rt-card" :class="{ 'rt-card-inuse': isActive(t) }">
        <span v-if="isActive(t)" class="rt-inuse-badge"><b-icon pack="fas" icon="circle-check" size="is-small" /> {{ activeModified ? $t('in_use_modified') : $t('in_use') }}</span>
        <div class="rt-thumb">
          <div v-for="(col, ci) in galleryThumb(t)" :key="ci" class="rt-gthumb">
            <span v-for="(l, li) in col" :key="li" class="rt-gl" :class="{ t: l.t }" :style="l.w ? { width: l.w } : null"></span>
          </div>
        </div>
        <div class="rt-body">
          <div class="rt-name-row">
            <span class="rt-name">{{ t.name }}</span>
            <span v-if="t.reco && !isActive(t)" class="rt-tag-reco">{{ $t('recommended') }}</span>
            <span class="rt-tag-type" :class="t.locked ? 'fija' : 'pers'">{{ t.locked ? $t('fixed') : $t('customizable') }}</span>
            <button v-if="t.info" type="button" class="ml-1 leading-none text-gray-400 hover:text-blue-600 transition-colors" :title="$t('information')" @click.stop="openInfo(t)">
              <b-icon pack="fas" icon="circle-info" size="is-small" />
            </button>
          </div>
          <div class="rt-desc">{{ t.desc }}</div>
          <div class="rt-chips">
            <span v-for="(c, ci) in t.chips" :key="ci" class="rt-chip">{{ c }}</span>
            <span class="rt-chip">{{ pagesFor(t) }} {{ $t('pages') }}</span>
          </div>
          <button type="button" class="rt-btn-primary mt-3 self-start" @click="$emit('use-template', t)">
            {{ $t('view_template') }}
          </button>
        </div>
      </div>

      <!-- Card "Cargar desde otro proyecto". Si hay una config importada EN USO, muestra
           de qué proyecto se copió y sus etiquetas. -->
      <div class="rt-card rt-card-special" :class="{ 'rt-card-inuse': importedFrom }">
        <span v-if="importedFrom" class="rt-inuse-badge"><b-icon pack="fas" icon="circle-check" size="is-small" /> {{ $t('in_use') }}</span>
        <div class="rt-thumb rt-thumb-special">
          <i class="fas fa-folder-open" aria-hidden="true"></i>
        </div>
        <div class="rt-body">
          <div class="rt-name-row">
            <span class="rt-name">{{ $t('import_from_project') }}</span>
          </div>
          <div class="rt-desc">
            <template v-if="importedFrom">{{ $t('copied_from') }}: <b>{{ importedFrom.name }}</b></template>
            <template v-else>{{ $t('import_from_project_desc') }}</template>
          </div>
          <div v-if="importedFrom && importedChips.length" class="rt-chips">
            <span v-for="(c, ci) in importedChips" :key="ci" class="rt-chip">{{ c }}</span>
          </div>
          <div class="rt-card-actions mt-3">
            <button v-if="importedFrom" type="button" class="rt-btn-ghost" @click.stop="$emit('view-imported')">{{ $t('view_template') }}</button>
            <button type="button" class="rt-btn-primary" @click.stop="$emit('import-existing')">{{ $t('open') }}</button>
          </div>
        </div>
      </div>

      <!-- Card "Personalizada": abre el Constructor de páginas (control total) -->
      <div class="rt-card rt-card-special" :class="{ 'rt-card-inuse': customInUse }" @click="$emit('open-builder')">
        <span v-if="customInUse" class="rt-inuse-badge"><b-icon pack="fas" icon="circle-check" size="is-small" /> {{ $t('in_use') }}</span>
        <div class="rt-thumb rt-thumb-special">
          <i class="fas fa-table-cells" aria-hidden="true"></i>
        </div>
        <div class="rt-body">
          <div class="rt-name-row">
            <span class="rt-name">{{ $t('custom_template') }}</span>
            <span class="rt-tag-type pers">{{ $t('advanced') }}</span>
          </div>
          <div class="rt-desc">{{ $t('page_builder_card_desc') }}</div>
          <div v-if="customInUse && customChips.length" class="rt-chips">
            <span v-for="(c, ci) in customChips" :key="ci" class="rt-chip">{{ c }}</span>
          </div>
          <button type="button" class="rt-btn-primary mt-3 self-start" @click.stop="$emit('open-builder')">
            {{ $t('open_builder') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de información de la plantilla (popover/modal, no tooltip) -->
    <epc-modal-wrapper :active.sync="infoOpen" :title="infoTemplate ? infoTemplate.name : $t('information')" :width="520">
      <template-info v-if="infoOpen && infoTemplate" :template="infoTemplate" @close="infoOpen = false" @use="useFromInfo" />
    </epc-modal-wrapper>
  </section>
</template>

<script>
import { templates, galleryThumb, pagesFor } from '../data/report-template.data'
import TemplateInfo from './TemplateInfo.vue'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'

export default {
  name: 'template-galery',

  components: { TemplateInfo, EpcModalWrapper },

  props: {
    activeKey: { type: String, default: null },       // plantilla en uso (weekly/executive/custom)
    activeModified: { type: Boolean, default: false },
    importedFrom: { type: Object, default: null },     // { id, name } si la config EN USO se copió de otro proyecto
    importedChips: { type: Array, default: () => [] }, // etiquetas de las páginas de la config EN USO
    customInUse: { type: Boolean, default: false },    // config en uso que no es plantilla con nombre ni importada
    customChips: { type: Array, default: () => [] },   // etiquetas de las páginas de esa config personalizada
  },

  data() {
    return { infoOpen: false, infoTemplate: null }
  },

  computed: {
    templatesArr() {
      // "custom" ya no es una card de plantilla: es la card "Personalizada" (el Constructor).
      return Object.keys(templates).filter((key) => key !== 'custom').map((key) => ({ key, ...templates[key] }))
    },
  },

  methods: {
    // funciones puras del data module (no usan this)
    galleryThumb,
    pagesFor,

    // ¿Esta tarjeta es la plantilla en uso del proyecto? (t.key ya es la clave canónica)
    isActive(t) {
      return !!this.activeKey && t.key === this.activeKey
    },

    openInfo(t) { this.infoTemplate = t; this.infoOpen = true },

    // Desde el modal de info: "Ver plantilla" → abre el customizer de esa plantilla.
    useFromInfo() {
      const t = this.infoTemplate
      this.infoOpen = false
      if (t) this.$emit('use-template', t)
    },
  },
}
</script>

<style scoped>
.rt-lead { color: var(--rt-muted); font-size: 13.5px; margin: 0 0 18px; max-width: 72ch; }

.rt-gallery { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.rt-card { position: relative; background: var(--rt-surface); border: 1px solid var(--rt-line); border-radius: var(--rt-radius); box-shadow: var(--rt-shadow); display: flex; flex-direction: column; overflow: hidden; transition: transform .12s, border-color .12s, box-shadow .12s; }
.rt-card:hover { transform: translateY(-2px); border-color: var(--rt-line-strong); }
.rt-card-special { cursor: pointer; border-style: dashed; }
.rt-card-special:hover { border-color: var(--rt-accent); }

/* Card EN USO: toda la tarjeta destaca (borde+anillo de acento, barra superior y realce) */
.rt-card-inuse { border: 1.5px solid var(--rt-accent); border-style: solid; box-shadow: 0 0 0 3px var(--rt-accent-wash), var(--rt-shadow); transform: translateY(-2px); }
.rt-card-inuse:hover { border-color: var(--rt-accent-ink); transform: translateY(-3px); }
.rt-card-inuse::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--rt-accent); z-index: 2; }
.rt-card-inuse .rt-thumb { background: var(--rt-accent-wash); }
.rt-inuse-badge { position: absolute; top: 10px; right: 10px; z-index: 3; display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #fff; background: var(--rt-accent); padding: 3px 9px 3px 7px; border-radius: 999px; box-shadow: 0 2px 6px rgba(20,32,60,.25); }
.rt-thumb-special { align-items: center; justify-content: center; color: var(--rt-accent); background: var(--rt-accent-wash); }
.rt-thumb-special .fas { font-size: 42px; opacity: .85; }
.rt-thumb { background: var(--rt-surface-2); border-bottom: 1px solid var(--rt-line); padding: 14px; display: flex; gap: 6px; height: 150px; overflow: hidden; }
.rt-gthumb { background: var(--rt-surface); border: 1px solid var(--rt-line-strong); border-radius: 3px; width: 46px; flex: none; padding: 4px; display: flex; flex-direction: column; gap: 3px; }
.rt-gl { height: 2.5px; border-radius: 2px; background: var(--rt-line-strong); }
.rt-gl.t { height: 4px; width: 60%; background: var(--rt-accent); opacity: .75; }
.rt-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; flex: 1; }
.rt-name-row { display: flex; align-items: center; gap: 8px; }
.rt-name { font-size: 16px; font-weight: 800; letter-spacing: -.01em; }
.rt-tag-reco { font-size: 10px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--rt-ok); background: color-mix(in srgb, var(--rt-ok) 14%, transparent); padding: 2px 8px; line-height: 1.3; border-radius: 999px; }
.rt-tag-type {font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; padding: 2px 8px; line-height: 1.3; border-radius: 999px; margin-left: auto; }
.rt-tag-type.fija { color: var(--rt-warn); background: color-mix(in srgb, var(--rt-warn) 14%, transparent); }
.rt-tag-type.pers { color: var(--rt-accent-ink); background: var(--rt-accent-wash); }
.rt-desc { color: var(--rt-muted); font-size: 12.5px; }
.rt-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: auto; }
.rt-chip { font-size: 11px; font-weight: 600; color: var(--rt-muted); background: var(--rt-surface-2); border: 1px solid var(--rt-line); border-radius: 6px; padding: 2px 7px; }
.rt-btn-primary { font: inherit; font-size: 13px; font-weight: 700; border-radius: 9px; padding: 9px 14px; cursor: pointer; border: 1px solid transparent; background: var(--rt-accent); color: #fff; }
.rt-btn-primary:hover { background: var(--rt-accent-ink); }
.rt-card-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.rt-btn-ghost { font: inherit; font-size: 13px; font-weight: 700; border-radius: 9px; padding: 9px 14px; cursor: pointer; background: transparent; color: var(--rt-accent-ink); border: 1px solid var(--rt-line-strong); }
.rt-btn-ghost:hover { background: var(--rt-accent-wash); border-color: var(--rt-accent); }
.mt-3 { margin-top: 12px; }
.self-start { align-self: flex-start; }

@media (max-width: 820px) {
  .rt-gallery { grid-template-columns: 1fr; }
}
</style>
